import { useCallback, useEffect, useRef, useState } from "react";
import { Keyboard, X } from "@phosphor-icons/react";

/**
 * TransliterateInput — Manglish-style phonetic input for Indian and other
 * complex scripts using Google Input Tools' free JavaScript API.
 *
 * How it works:
 *   • User types phonetically in Latin script (e.g. "namaste")
 *   • On each token boundary (space, comma, punctuation, blur), we call
 *     `https://inputtools.google.com/request?text=<latin>&itc=<code>-t-i0-und&num=5`
 *   • The API returns up to 5 candidate suggestions in the target script.
 *   • Selecting a suggestion (or hitting space) replaces the Latin token
 *     with the top candidate.
 *   • Users can override via a dropdown that appears just below the cursor.
 *
 * Used inside the SocialPulse Publisher editor + Ask-an-Expert forms so
 * writers can compose in Malayalam / Hindi / Tamil / Bengali etc. without
 * installing a system-level IME.
 */

// Google Input Tools codes — only enable the scripts we actually support.
export const TRANSLITERATE_LANGS = [
  { code: "off", label: "English (no transliteration)", api: null },
  { code: "ml", label: "Malayalam · മലയാളം", api: "ml-t-i0-und" },
  { code: "hi", label: "Hindi · हिन्दी", api: "hi-t-i0-und" },
  { code: "ta", label: "Tamil · தமிழ்", api: "ta-t-i0-und" },
  { code: "te", label: "Telugu · తెలుగు", api: "te-t-i0-und" },
  { code: "kn", label: "Kannada · ಕನ್ನಡ", api: "kn-t-i0-und" },
  { code: "bn", label: "Bengali · বাংলা", api: "bn-t-i0-und" },
  { code: "mr", label: "Marathi · मराठी", api: "mr-t-i0-und" },
  { code: "gu", label: "Gujarati · ગુજરાતી", api: "gu-t-i0-und" },
  { code: "pa", label: "Punjabi · ਪੰਜਾਬੀ", api: "pa-t-i0-und" },
  { code: "or", label: "Odia · ଓଡ଼ିଆ", api: "or-t-i0-und" },
  { code: "ne", label: "Nepali · नेपाली", api: "ne-t-i0-und" },
  { code: "ar", label: "Arabic · العربية", api: "ar-t-i0-und" },
  { code: "fa", label: "Persian · فارسی", api: "fa-t-i0-und" },
  { code: "sa", label: "Sanskrit · संस्कृतम्", api: "sa-t-i0-und" },
];

// One shared cache across every mounted input so we never call Google
// twice for the same (token, language) pair.
const CACHE = new Map();

async function fetchSuggestions(token, api) {
  if (!token || !api) return [];
  const key = `${api}::${token.toLowerCase()}`;
  if (CACHE.has(key)) return CACHE.get(key);
  try {
    const url = `https://inputtools.google.com/request?text=${encodeURIComponent(token)}&itc=${api}&num=5&cp=0&cs=1&ie=utf-8&oe=utf-8&app=demopage`;
    const r = await fetch(url);
    const data = await r.json();
    // Response shape: ["SUCCESS", [[latin, [candidates...], ...]]]
    if (data?.[0] !== "SUCCESS") return [];
    const first = data?.[1]?.[0];
    const cands = Array.isArray(first?.[1]) ? first[1] : [];
    CACHE.set(key, cands);
    return cands;
  } catch (_e) {
    return [];
  }
}

export default function TransliterateInput({
  as = "input",
  value,
  onChange,
  rows = 4,
  placeholder,
  testid = "translit-input",
  className = "",
  langCode: propLangCode,
  onLangChange,
}) {
  const [lang, setLang] = useState(propLangCode || "off");
  useEffect(() => { if (propLangCode) setLang(propLangCode); }, [propLangCode]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [candidates, setCandidates] = useState({ visible: false, list: [], token: "", pos: 0 });
  const inputRef = useRef(null);

  const langCfg = TRANSLITERATE_LANGS.find((l) => l.code === lang) || TRANSLITERATE_LANGS[0];
  const isOn = !!langCfg.api;

  const replaceRange = useCallback((start, end, replacement) => {
    const el = inputRef.current;
    if (!el) return;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const next = before + replacement + after;
    onChange({ target: { value: next } });
    // Move caret to end of replacement on next tick.
    requestAnimationFrame(() => {
      const caret = before.length + replacement.length;
      try { el.setSelectionRange(caret, caret); } catch (_e) { /* noop */ }
    });
  }, [value, onChange]);

  // On keydown for space / punctuation → commit the trailing Latin token.
  const onKeyDown = async (e) => {
    if (!isOn) return;
    const commitChars = [" ", ",", ".", "!", "?", ";", ":", "\n", "\t"];
    if (!commitChars.includes(e.key)) return;
    const el = inputRef.current;
    if (!el) return;
    const caret = el.selectionStart ?? value.length;
    // Find start of the current Latin token (whitespace-delimited).
    const upto = value.slice(0, caret);
    const m = upto.match(/([A-Za-z][A-Za-z']*)$/);
    if (!m) return;
    const token = m[1];
    const start = caret - token.length;
    const list = await fetchSuggestions(token, langCfg.api);
    if (!list.length) return;
    // Insert the top candidate immediately + open a small "did you mean"
    // menu with the alternatives so the writer can override.
    replaceRange(start, caret, list[0]);
    setCandidates({
      visible: true, list, token,
      pos: start + list[0].length,
    });
    setTimeout(() => setCandidates((c) => ({ ...c, visible: false })), 4500);
  };

  const commonProps = {
    ref: inputRef,
    value: value ?? "",
    onChange: (e) => onChange({ target: { value: e.target.value } }),
    onKeyDown,
    placeholder,
    "data-testid": `${testid}-field`,
    className: (
      "w-full bg-white border border-black/15 rounded-lg px-3 py-2 pr-24 " +
      (as === "textarea" ? "text-sm min-h-[7rem] " : "text-base ") +
      "focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)] " +
      className
    ),
  };

  return (
    <div className="relative" data-testid={testid}>
      {as === "input" ? <input type="text" {...commonProps} /> : <textarea rows={rows} {...commonProps} />}

      {/* Language picker + IME toggle */}
      <div className="absolute right-1.5 bottom-1.5 flex gap-1">
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid={`${testid}-lang-btn`}
            className={
              "inline-flex items-center gap-1 rounded-full backdrop-blur px-2 py-0.5 " +
              "font-mono-tech text-[9px] tracking-[0.22em] uppercase border " +
              (isOn
                ? "border-[color:var(--np-brand,#B85F2A)] bg-[color:var(--np-brand,#B85F2A)] text-white"
                : "border-black/15 bg-white/85 text-[#241209]")
            }
            title="Toggle phonetic Indian-script input (Google Input Tools)"
          >
            <Keyboard size={10} weight="fill" /> {lang.toUpperCase()}
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 bottom-full mb-1 max-h-64 w-64 overflow-y-auto rounded-xl border border-black/10 bg-white shadow-lg py-1 z-50"
              data-testid={`${testid}-lang-menu`}
            >
              {TRANSLITERATE_LANGS.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => { setLang(l.code); setMenuOpen(false); onLangChange?.(l.code); }}
                  data-testid={`${testid}-lang-${l.code}`}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-black/5 ${lang === l.code ? "bg-[#FBF2E1] font-semibold" : ""}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Candidate overrides — a small "did you mean" bar below the field */}
      {candidates.visible && candidates.list.length > 1 && (
        <div className="mt-1 flex items-center gap-1 flex-wrap" data-testid={`${testid}-candidates`}>
          <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">
            for "{candidates.token}":
          </span>
          {candidates.list.slice(0, 5).map((c, i) => (
            <button
              key={c + i}
              type="button"
              onClick={() => {
                // Replace the just-inserted top candidate at `pos` with this one.
                const before = value.slice(0, candidates.pos - candidates.list[0].length);
                const after = value.slice(candidates.pos);
                onChange({ target: { value: before + c + after } });
                setCandidates((s) => ({ ...s, visible: false }));
              }}
              data-testid={`${testid}-cand-${i}`}
              className={
                "rounded-full px-2 py-0.5 text-xs border " +
                (i === 0
                  ? "border-[color:var(--np-brand,#B85F2A)] bg-[#FBF2E1]"
                  : "border-black/15 bg-white hover:bg-black/5")
              }
            >
              {c}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setCandidates((s) => ({ ...s, visible: false }))}
            className="rounded-full px-1 py-0.5 text-xs text-[#3A3830] hover:text-black"
            title="Dismiss"
          >
            <X size={10} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
}
